import type { RuleFormData } from "../../components/forms/BuildingForm";
import type { Customer, CustomerSiteSummary } from "../../services/customer/customer.types";
import { createRule, listSiteRules } from "../../services/ruleManagement/ruleManagement.service";
import type { CreateRuleRequest, RuleDetail, RuleSummary, UpdateRuleRequest } from "../../services/ruleManagement/ruleManagement.types";

export interface CustomerWithSite extends Customer {
  primarySite?: CustomerSiteSummary;
  siteId?: number;
  siteHierarchyId?: number;
}

export type ConfirmBuildingAction = (heading: string, message: string, action: () => Promise<void>) => void;

export const getBuildingErrorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : String(error);

const splitCsv = (value?: string): string[] =>
  (value || "").split(",").map(item => item.trim()).filter(Boolean);

const toNumberList = (value?: string): number[] => splitCsv(value).map(Number).filter(Boolean);

type CompatibleRuleData = Omit<RuleFormData, "filteringPriorities"> & {
  filteringPriorities?: number[] | string;
  filteringClasses?: string | string[];
  filteringPatterns?: string | string[];
};

const toStringList = (value?: string | string[]): string[] =>
  Array.isArray(value) ? value.map(item => item.trim()).filter(Boolean) : splitCsv(value);

export function buildRuleUpdatePayload(data: CompatibleRuleData): UpdateRuleRequest {
  return {
    ruleName: data.ruleName,
    isActive: !!data.isActive,
    bindPathPriority: !!data.bindPathPriority,
    filteringCriteria: {
      pathIds: toNumberList(data.filteringPaths),
      priorities: Array.isArray(data.filteringPriorities) ? data.filteringPriorities.filter(Boolean) : toNumberList(data.filteringPriorities),
      classes: toStringList(data.filteringClasses),
      patterns: toStringList(data.filteringPatterns),
    },
    distributionGroupIds: toNumberList(data.distributionGroupIds),
    attachmentGroupIds: toNumberList(data.attachmentGroupIds),
    behavior: { hideRtn: !!data.hideRtn, rtnExceptions: splitCsv(data.rtnExceptions) },
    isUltraCritical: data.isUltraCritical ?? false,
  };
}

export function buildRulePayload(siteId: number, siteHierarchyId: number | undefined, data: CompatibleRuleData): CreateRuleRequest {
  if (!siteHierarchyId || siteHierarchyId <= 0) {
    throw new Error("Cannot create rule: siteHierarchyId is required and must be > 0");
  }
  if (!siteId || siteId <= 0) {
    throw new Error("Cannot create rule: siteId is required and must be > 0");
  }
  return {
    ...buildRuleUpdatePayload(data),
    siteIdentifierRegistryId: siteId,
    siteHierarchyId,
    entityType: "Site",
    ruleName: data.ruleName || `Rule for site ${siteHierarchyId}`,
  };
}

export const mapRuleDetailToFormData = (rule: RuleDetail, siteId?: number): RuleFormData => ({
  ruleName: rule.ruleName || "",
  isActive: rule.isActive ?? false,
  bindPathPriority: rule.bindPathPriority ?? true,
  distributionGroupIds: (rule.distributionGroups || []).map(group => group.distributionGroupId).join(","),
  attachmentGroupIds: (rule.attachmentGroups || []).map(group => group.attachmentGroupId).join(","),
  appliesToSiteIds: siteId ? String(siteId) : "",
  filteringPaths: (rule.alarmPaths || []).map(path => path.id).join(","),
  filteringPriorities: rule.priorities || [],
  hideRtn: rule.hideRtn ?? false,
  rtnExceptions: (rule.rtnExceptions || []).join(","),
  isUltraCritical: rule.isUltraCritical ?? false,
});

export const mapRuleDetailToSummary = (rule: RuleDetail): RuleSummary => ({
  siteRuleId: rule.siteRuleId,
  siteHierarchyId: rule.siteHierarchyId,
  siteName: rule.siteName,
  siteIdentifier: rule.siteIdentifier,
  ruleName: rule.ruleName,
  isActive: rule.isActive,
  isUltraCritical: rule.isUltraCritical,
  bindPathPriority: rule.bindPathPriority,
  hideRtn: rule.hideRtn,
  distributionGroupCount: rule.distributionGroupCount,
  attachmentGroupCount: rule.attachmentGroupCount,
  createdAt: rule.createdAt,
  updatedAt: rule.updatedAt,
});

export async function loadBuildingRules(siteId?: number): Promise<RuleSummary[]> {
  if (!siteId) return [];
  const response = await listSiteRules(siteId);
  if (!response.success || !response.data) {
    throw new Error(response.error?.message || "Failed to load rules");
  }
  return response.data.siteRules;
}

export async function createBuildingRules(customer: CustomerWithSite, rules: RuleFormData[]): Promise<string[]> {
  const failures: string[] = [];
  for (const [index, rule] of rules.entries()) {
    try {
      const payload = buildRulePayload(customer.siteId ?? 0, customer.siteHierarchyId, rule);
      const response = await createRule(payload);
      if (!response.success) throw new Error(response.error?.message || "Failed to create rule");
    } catch (error: unknown) {
      failures.push(`Rule ${index + 1} (${rule.ruleName || "unnamed"}): ${getBuildingErrorMessage(error)}`);
    }
  }
  return failures;
}
